/* eslint-disable react/prop-types */
import DownloadIcon from '@mui/icons-material/Download';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import TuneIcon from '@mui/icons-material/Tune';
import { deleteOrderGuide, updateOrderGuidePricing } from '../../api/order-guides';
import { HOST_API_URL } from '../../config-global';
import { renderColorBox } from '../../utils/cssStyles';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency } from '../../utils/formatNumber';
import { fMonthDayYearShort, fMonthDayTimeYear } from '../../utils/formatTime';
import { ConfirmDialog } from '../confirm-dialog';
import Iconify from '../iconify';
import Image from '../image';
import Lightbox from '../lightbox';
import { ScopeGuard } from '../scopes';
import OrderGuideVendorPreferences from '../../components/dashboard/admin/order-guides/OrderGuideVendorPreferences';

const renderIcon = (value, icon) => (
  <Box>
    <Iconify icon={icon} width={13} height={13} /> {value}
  </Box>
);
const downloadGuide = async (rowId) => {
  //currently this is not working on dev environment as HOST_API_URL is not set but it will work on production
  window.open(`${HOST_API_URL}Api/DownloadOrderGuide?orderGuideActivationId=${rowId}`, '_blank');
};
const handleDelete = async (rowId) => {
  const response = await deleteOrderGuide(rowId);
  if (response.status === 200) {
    window.location.reload();
  }
};
const handleRefresh = async (rowId) => {
  await updateOrderGuidePricing(rowId);
};

const facility = {
  field: 'facility',
  headerName: 'Facility',
  minWidth: 90,
  type: 'customSelect',
  flex: 1,
};
const group = {
  field: 'group',
  headerName: 'Group',
  minWidth: 90,
  type: 'customSelect',
  flex: 1
};

const active = {
  field: 'active',
  headerName: 'Active',
  minWidth: 50,
  renderCell: (params) => {
    const { value } = params;
    return value ? (
      <Iconify icon="material-symbols:check-circle-outline" color="primary" />
    ) : (
      <Iconify icon="mdi:close" color="error" />
    );
  },
  type: 'boolean',
  valueOptions: [true, false],
};

const department = {
  field: 'agoraCategory',
  headerName: 'Department',
  minWidth: 100,
  flex: 1, 
  renderCell: (params) => renderColorBox(params.row.agoraCategory, 'secondary'),
  valueGetter: (params) => params.row.agoraCategory,
  type: 'customSelect',
};

const category = {
  field: 'category',
  headerName: 'Category',
  width: 100,
  minWidth: 100,
  renderCell: (params) => renderColorBox(params.row.agoraCategory?.name, 'secondary'),
  valueGetter: (params) => params.row.agoraCategory?.name,
  type: 'customSelect',
  path: 'agoraCategory.name',
};
const agoraCategory = {
  field: 'agoraCategory',
  headerName: 'Category',
  minWidth: 100,
  renderCell: (params) => renderColorBox(params.row.agoraCategory, 'secondary'),
  valueGetter: (params) => params.row.agoraCategory?.name,
  type: 'customSelect',
  path: 'agoraCategory.name',
};

const items = {
  field: 'count',
  headerName: 'Items',
  minWidth: 80,
  flex: 0.7
};

const dateAdded = {
  field: 'dateAdded',
  headerName: 'Date Created',
  minWidth: 80,
  flex: 1,
  renderCell: (params) =>
    renderIcon(`${fMonthDayTimeYear(params.value)} EST`, 'material-symbols:calendar-today-outline'),
  type: 'customDate',
  valueGetter: (params) => new Date(params.value),
};
const ActionButtons = (params) => {
  const [isVendorPreferencesOpen, setIsVendorPreferencesOpen] = useState(false);
  const { locked } = params.row;
  return (
    <>
      {' '}
      <Tooltip title="Download" arrow>
        <Box id="action-button" className="action-button">
          <IconButton
            color="info"
            onClick={() => downloadGuide(params.id)}
            id="action-button"
            className="action-button"
          >
            <DownloadIcon id="action-button" className="action-button" />
          </IconButton>
        </Box>
      </Tooltip>
      <ScopeGuard scopes={[' Edit-OrderGuide']} allowAdmin>
        {!locked && <>
          <Tooltip title="Vendor Preferences" arrow>
            <IconButton color="secondary" id="action-button" className="action-button" onClick={() => setIsVendorPreferencesOpen(true)}>
              <TuneIcon id="action-button" className="action-button" fontSize="small" />
            </IconButton>
          </Tooltip>

          <OrderGuideVendorPreferences orderGuideId={params.row.id} openVendorPreferences={isVendorPreferencesOpen} setOpenVendorPreferences={setIsVendorPreferencesOpen} />
        </>}
        {!locked && (
          <ConfirmDialog
            icon="refresh"
            title="Refresh Pricing"
            color="warning"
            content="Are you sure you want to fetch prices for this order guide? This may take some time."
            actionButton="Refresh"
            tooltip="Refresh Pricing"
            action={() => handleRefresh(params.id)}
          />
        )}
        {!locked && (
          <ConfirmDialog
            icon="delete-outline"
            title="Delete"
            color="error"
            content="Are you sure want to delete?"
            actionButton="Delete"
            action={() => handleDelete(params.id)}
            tooltip="Delete"
          />
        )}
      </ScopeGuard>
    </>
  );
};

const actionButtons = (params) => <ActionButtons {...params} />;

const getProductDetails = (params, value) => {
  const productDetails = params.row.activeProductDetail
    ? params.row.activeProductDetail
    : params.row.productDetails[0];
  return productDetails[value];
};
const RenderImage = ({ params }) => {
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  const pictures = params.row.activeProductDetail?.pictures || params.row.productDetails?.map((p) => p.pictures);

  return (
    <>
      <Image
        alt="attachment"
        src={
          pictures?.length
            ? cloudinaryThumbnail(pictures?.[0] || 'Item3.jpg')
            : cloudinaryThumbnail(
                'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
              )
        }
        sx={{
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
};

const picture = {
  field: 'picture',
  headerName: 'Image',
  width: 75,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};

const AvailableCell = ({ id }) => {
  const {
    orderGuide: { orderGuide },
  } = useSelector((state) => state.orderGuides.data);
  const cellIsAvailable = (id, orderGuide) => {
    const available = orderGuide?.find((item) => item.productId === id && item.available);
    if (available) {
      return <Iconify icon="mdi:check" color="primary" width={28} height={28} />;
    }
    return null;
  };
  return cellIsAvailable(id, orderGuide);
};

const available = {
  field: 'available',
  headerName: 'Available',
  minWidth: 40,
  flex: 1,
  renderCell: (params) => <AvailableCell id={params.row.id} />,
  sortable: false,
};
const addedBy = {
  field: 'addedBy',
  headerName: 'Added By',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {params.row.addedBy}
    </Box>
  ),
};
const addedDate = {
    field: 'dateAdded',
    headerName: 'Added Date',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) => params.row.activeTempOrderGuide?.dateAdded
        ? new Date(params.row.activeTempOrderGuide.dateAdded) : null,
    renderCell: (params) => params.row.activeTempOrderGuide?.dateAdded
        ? fMonthDayYearShort(params.row.activeTempOrderGuide.dateAdded) : '',
    type: 'customDate',
    filterable: false,
};
const deletedBy = {
  field: 'deletedBy',
  headerName: 'Removed By',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {
        params.row.activeTempOrderGuide?.deletedBy
      }
    </Box>
  ),
  sortable: true,
  valueGetter: (params) => params.row.activeTempOrderGuide?.deletedBy,
  path: 'activeTempOrderGuide.deletedBy',
  type: 'customSelect',
};
const deletedDate = {
  field: 'dateDeleted',
  headerName: 'Removed Date',
  minWidth: 120,
  flex: 1,
  type: 'customDate',
  renderCell: (params) => (
    <Box component="div" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', }}>
      {fMonthDayTimeYear(params.row.activeTempOrderGuide?.dateDeleted)}
    </Box>
  ),
  filterable: false,
};
const subcategory = {
  field: 'subcategory',
  headerName: 'Subcategory',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => params.row.agoraSubcategory?.name,
  valueGetter: (params) => params.row.agoraSubcategory?.name,
  type: 'customSelect',
  path: 'agoraSubcategory.name',
};

const description = {
  field: 'description',
  headerName: 'Description',
  minWidth: 100,
  flex: 1.25,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const manufacturer = {
  field: 'manufacturer',
  headerName: 'Manufacturer',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
  path: 'activeProductDetail.manufacturer',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {params.row.activeProductDetail?.manufacturer}
    </Box>
  ),
  valueGetter: (params) => params.row.activeProductDetail?.manufacturer,
};
const manufacturerId = {
  field: 'manufacturerId',
  headerName: 'Manufacturer Id',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
  path: 'activeProductDetail.manufacturerId',
  valueGetter: (params) => params.row.activeProductDetail?.manufacturerId,
};
const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
  path: 'activeProductDetail.vendor',
  valueGetter: (params) => params.row.activeProductDetail?.vendor,
};

const templateName = {
  field: 'templateName',
  headerName: 'Name',
  minWidth: 80,
  flex: 1,
  type: 'customSelect',
};

const sku = {
  field: 'vendorItemId',
  headerName: 'SKU',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.2,
      }}
    >
      {getProductDetails(params, 'vendorItemId')}
    </div>
  ),
  valueGetter: (params) => getProductDetails(params, 'vendorItemId'),
  getFilterValues: (row) =>  [getProductDetails({row}, 'vendorItemId')],
  type: 'customSelect',
};

const vendorSkus = {
  field: 'productDetails',
  headerName: 'Vendors/SKUs',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => (
    <Tooltip
      title={
        <>
          {params.row.productDetails.map((prodDetails) => (
            <Box key={prodDetails.id}>
              {' '}
              {prodDetails.vendor} | {prodDetails.vendorItemId}{' '}
            </Box>
          ))}
        </>
      }
    >
      <Box>
        {params.row.productDetails.slice(0, 2).map((prodDetails) => (
          <Box key={prodDetails.id}>
            {' '}
            {prodDetails.vendor} | {prodDetails.vendorItemId}{' '}
          </Box>
        ))}
      </Box>
    </Tooltip>
  ),
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.productDetails?.map((prodDetails) => `${prodDetails.vendor} | ${prodDetails.vendorItemId}`),
  valueGetter: (params) => params.row.productDetails.map((prodDetails) => `${prodDetails.vendor} | ${prodDetails.vendorItemId}`).join(", ") ?? '',
  type: 'customSelect',
};

const uom = {
  field: 'uom',
  headerName: 'UOM',
  minWidth: 70,
  flex: 1,
  renderCell: (params) => {
    const uomAmount = getProductDetails(params, 'uomAmount');
    const uomType = getProductDetails(params, 'uomType');
    return `${uomAmount}/${uomType}`;
  },
  valueGetter: (params) => {
    const uomAmount = getProductDetails(params, 'uomAmount');
    const uomType = getProductDetails(params, 'uomType');
    return `${uomAmount}/${uomType}`;
  },
  type: 'customSelect',
  getFilterValues: (row) => {
    const uomAmount = getProductDetails({row}, 'uomAmount');
    const uomType = getProductDetails({row}, 'uomType');
    return [`${uomAmount}/${uomType}`];
  }
};

const price = {
  field: 'price',
  headerName: 'Price',
  minWidth: 75,
  flex: 0.8,
  renderCell: (params) => fCurrency(getProductDetails(params, 'price')),
  valueGetter: (params) => getProductDetails(params, 'price'),
};

const actions = {
  field: 'actions',
  headerName: 'Actions',
  disableReorder: true,
  minWidth: 160,
  renderCell: (params) => actionButtons(params),
  id: 'actions',
  className: 'actions',
  flex: 0.8
};
const id = {
  field: 'id',
  valueGetter: (params) => `PS${params.row.id}`,
  getFilterValues: (row) => [`PS${row.id}`],
  headerName: 'PS ID',
  minWidth: 80,
  flex: 1,
  type: 'customSelect',
};

const ORDER_GUIDES = [active, facility, group, department, items, dateAdded, addedBy, actions];

const ALL_PRODUCTS = [
  picture,
  description,
  sku,
  uom,
  price,
  manufacturer,
  manufacturerId,
  vendor,
  subcategory,
  category,
  id,
  available,
  addedBy,
  addedDate,
];
const INACTIVE_PRODUCTS = [
  picture,
  description,
  sku,
  uom,
  price,
  manufacturer,
  manufacturerId,
  vendor,
  subcategory,
  category,
  id,
  available,
  deletedBy,
  deletedDate,
];
const MANAGE_ITEMS = [picture, description, vendorSkus, subcategory, id];
const TEMPLATES = [templateName, agoraCategory, items, dateAdded];

export { ORDER_GUIDES, INACTIVE_PRODUCTS, ALL_PRODUCTS, TEMPLATES, MANAGE_ITEMS };
